/**
 * Loader config self-discovery — T-311.
 *
 * Reads `data-*` attrs from the loader's own <script> tag. Form 1
 * snippet (stamped 2026-05-01): single script element, no programmatic
 * init API, no custom mount point.
 *
 *   <script
 *     src="https://muse-noir.web.app/loader.js"
 *     data-tenant="muse-optical"
 *     data-api="https://qbxzeqfp97.execute-api.us-east-1.amazonaws.com"
 *     async
 *   ></script>
 *
 * Both attrs are required for production use. Local dev / sandbox can
 * paste without `data-api` and the loader degrades to a no-probe
 * mount (matches prod loader fallback for legacy embeds).
 */

export interface LoaderConfig {
  tenantId: string;
  apiBase: string;       // empty string = no health probe (legacy path)
  cdnBase: string;       // origin from which loader.js was served
  cdnOrigin: string;     // origin only, for postMessage validation
  iframeSrc: string;     // resolved iframe URL (empty if cdnBase missing)
}

export function discoverConfig(): LoaderConfig | null {
  const scriptEl =
    document.currentScript ||
    document.querySelector<HTMLScriptElement>("script[data-tenant]");

  if (!scriptEl) {
    // No script tag with data-tenant — can't self-discover; bail rather
    // than mount with bad defaults.
    if (typeof console !== "undefined") {
      console.error("[Muse] loader: no <script data-tenant> element found");
    }
    return null;
  }

  const tenantId = scriptEl.getAttribute("data-tenant") || "muse-optical";
  const apiBase = scriptEl.getAttribute("data-api") || "";

  // Derive CDN base from the loader's own src attribute. Used for the
  // iframe URL (loader.js and index.html ship from the same Firebase
  // Hosting site) and for postMessage origin validation.
  const scriptSrc = (scriptEl as HTMLScriptElement).src;
  const cdnBase = scriptSrc ? new URL(".", scriptSrc).href : "";
  const cdnOrigin = scriptSrc ? new URL(scriptSrc).origin : "";

  // The widget index.html lives at the loader's CDN root.
  // Pass tenant + api as query params; TenantConfigProvider reads
  // them to bootstrap config.
  let iframeSrc = "";
  if (cdnBase) {
    const params = new URLSearchParams({ tenant: tenantId });
    if (apiBase) params.set("api", apiBase);
    iframeSrc = `${cdnBase}index.html?${params.toString()}`;
  }

  return { tenantId, apiBase, cdnBase, cdnOrigin, iframeSrc };
}
